import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface VaultRenameDialogProps {
  open: boolean;
  initialName: string;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}

export function VaultRenameDialog({ open, initialName, onClose, onSave }: VaultRenameDialogProps) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setError(null);
    }
  }, [open, initialName]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Document name is required.');
      return;
    }
    if (trimmed === initialName.trim()) {
      onClose();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await onSave(trimmed);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not rename document.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wfx-vault-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-vault-modal glass-surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vault-rename-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-vault-modal-head">
          <h2 id="vault-rename-title">Rename document</h2>
          <button type="button" className="ghost-icon-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        <form className="wfx-vault-upload-form" onSubmit={handleSubmit}>
          {error && <div className="auth-error" role="alert">{error}</div>}
          <label className="wfx-vault-select">
            <span>Document name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              maxLength={120}
            />
          </label>
          <div className="wfx-vault-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={loading}>
              {loading ? 'Saving…' : 'Save name'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
